import { createMemo, createSignal, Match, Show, Switch } from 'solid-js'
import { BrowseView } from './components/BrowseView.tsx'
import { FileView } from './components/FileView.tsx'
import { RepoList } from './components/RepoList.tsx'
import { ResultsPanel } from './components/ResultsPanel.tsx'
import { focusSearch, SearchBar } from './components/SearchBar.tsx'
import { PendingPrefix, ShortcutsDialog } from './components/ShortcutsDialog.tsx'
import { ThemeToggle } from './components/ThemeToggle.tsx'
import { UserMenu } from './components/UserMenu.tsx'
import { setSearchQuery, useIndexerStatus } from './store/search.ts'
import { useShortcuts } from './store/shortcuts.ts'
import { currentLocation, goHome, route } from './store/view.ts'
import { cn } from './utils/cn.ts'

export function App() {
  const status = useIndexerStatus()
  const isHome = createMemo(() => route().kind === 'HOME')
  const [showShortcuts, setShowShortcuts] = createSignal(false)

  useShortcuts({
    '?': () => setShowShortcuts(it => !it),
    '/': () => focusSearch(),
    '.': () => {
      // code search's "search in this directory". the directory becomes a `path:`
      // filter, which is a normal part of the query language you can then edit
      const here = currentLocation()
      if (!here) return
      const dir = here.path.includes('/') ? here.path.replace(/\/[^/]*$/, '') : ''
      setSearchQuery(dir ? `path:${dir} ` : '')
      focusSearch(false)
    },
    'Escape': () => setShowShortcuts(false),
  })

  return (
    <div class="relative h-full flex flex-col">
      <ShortcutsDialog open={showShortcuts()} onOpenChange={setShowShortcuts} />
      <PendingPrefix />

      {/* pinned to the corner in both layouts, so nothing here ever remounts */}
      <div class="absolute right-2 top-2 z-10 flex items-center gap-1">
        <ThemeToggle />
        <UserMenu />
      </div>

      {/*
        the landing hero and the app bar are the same DOM, only restyled. rendering
        them as two branches would unmount the search input on the first keystroke,
        which drops the caret and makes you click the box again to keep typing
      */}
      <div
        class={cn(
          'flex gap-3',
          isHome()
            ? 'flex-1 flex-col items-center justify-center px-4'
            : 'shrink-0 items-center border-b border-border p-1.5',
        )}
      >
        <div
          class={cn('flex items-center gap-2', !isHome() && 'shrink-0 cursor-pointer')}
          onClick={() => !isHome() && goHome()}
        >
          <div class="i-lucide-search-code size-5 shrink-0 text-muted-foreground" />
          <span class="text-sm font-bold">codesearch</span>
        </div>

        <div class={isHome() ? 'w-full max-w-2xl' : 'max-w-3xl min-w-0 flex-1'}>
          <SearchBar large={isHome()} />
        </div>

        <div
          class={cn(
            'items-center gap-2 text-xs text-muted-foreground',
            isHome() ? 'flex' : 'hidden shrink-0 md:flex',
          )}
        >
          <Switch>
            <Match when={status.isError}>
              <span class="text-destructive">indexer unreachable</span>
            </Match>
            <Match when={status.data?.scanning}>
              <div class="i-lucide-loader-circle size-3 animate-spin" />
              <span>
                scanning
                {/* the counters are global, so they only arrive for someone who can
                    see every index; everyone else just gets told it's busy */}
                <Show when={status.data!.total > 0}>
                  {' '}
                  {status.data!.scanned}
                  /
                  {status.data!.total}
                </Show>
              </span>
            </Match>
            <Match when={status.data}>
              <span>
                {status.data!.files.toLocaleString()}
                {' files'}
              </span>
              <Show when={status.data!.failed > 0}>
                <span class="text-destructive">
                  {status.data!.failed}
                  {' failed'}
                </span>
              </Show>
            </Match>
          </Switch>
        </div>

        <Show when={isHome()}>
          <RepoList />
        </Show>
      </div>

      <Show when={!isHome()}>
        <div class="min-h-0 flex-1">
          <Switch>
            <Match when={route().kind === 'SEARCH'}>
              <ResultsPanel />
            </Match>
            <Match when={route().kind === 'BROWSE'}>
              <BrowseView />
            </Match>
            <Match when={route().kind === 'VIEW'}>
              <FileView />
            </Match>
          </Switch>
        </div>
      </Show>
    </div>
  )
}
